import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

import type { FadeEdge } from '../../utilities/shared/FadeMask';
import type { SectionIDProp, SectionPropRelation, SectionPropSize } from './Section';
import { useLocalStorage } from '../../utilities/useLocalStorage';

export type SectionCollapsedProp = boolean;
export type SectionFadeProp = FadeEdge;

interface SectionContextData {
  id: SectionIDProp;
  collapsible: boolean;
  collapsed: SectionCollapsedProp;
  setCollapsed: (value: SectionCollapsedProp) => void;
  relation: SectionPropRelation;
  size: SectionPropSize;
  fade: SectionFadeProp;
}

const SectionContext = createContext<SectionContextData>({
  id: '',
  collapsible: false,
  collapsed: false,
  setCollapsed: () => {},
  relation: 'standalone',
  size: 'medium',
  fade: 'vertical',
});

export function SectionProvider({
  children,
  collapsible,
  id,
  relation,
  size,
  fade,
}: {
  collapsible?: boolean;
  children?: ReactNode;
  id?: SectionIDProp;
  relation: SectionPropRelation;
  size: SectionPropSize;
  fade?: SectionFadeProp;
}) {
  const [collapsedState, setCollapsedState] = useLocalStorage<SectionCollapsedProp>(
    `atto.section.collapsed.${id}`,
    false,
  );

  const isCollapsible = collapsible ?? false;
  const value = useMemo(
    () => ({
      collapsible: isCollapsible,
      collapsed: isCollapsible && collapsedState,
      /**
       * ^ if we ever do ui iterations where:
        - there was at one point a collapsible section
        - a user collapses it said section
        - then we remove being able to collapse it from the UI

        the user would not be able to expand it back, this makes sure that does not happen :D
       */
      id: id || '',
      setCollapsed: setCollapsedState,
      relation: relation || 'standalone',
      size: size || 'medium',
      fade: fade ?? 'vertical',
    }),
    [isCollapsible, collapsedState, id, relation, size, setCollapsedState, fade],
  );

  return <SectionContext.Provider value={value}>{children}</SectionContext.Provider>;
}

export function useSectionContext() {
  return useContext(SectionContext);
}
